import { useEffect, useState } from "react";
import { ArrowLeft, IndianRupee, CheckCircle, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authService from "../Services/authService";
import AppLayout from "./AppLayout";

const HandoverDue = () => {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({ total_due_amount: 0, cash_collection_agents: [] });

  const navigate = useNavigate();

  useEffect(() => {
    loadDueAmount();
  }, []);

  const loadDueAmount = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + "cashcollectagents");

    fetch(url, {
      method: "POST",
      body: JSON.stringify({
        assign_to: authService.getCurrentuser().id,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        if (result && result.data) {
          setData(result.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load due amount:", err.message);
      });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!selectedAgent) return;

  setIsSubmitting(true);

  try {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + "dueamountsettlement");

    const payload = {
      phlebotomist_id:authService.getCurrentuser().id,
      cash_collection_agent_id: selectedAgent,
      due_amount: data.total_due_amount,
      order_ids: data.order_ids, // assuming it's a comma-separated string
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      authService.clientlogout();
      window.location.href = "/login";
      return;
    }

    const result = await response.json();

    // Assuming success:
    setIsSubmitting(false);
    setSubmitted(true);
  } catch (err) {
    console.error("Failed to submit handover:", err.message);
    setIsSubmitting(false);
  }
};


  const selectedAgentDetails = data.cash_collection_agents?.find(
    (agent) => agent.user_id === parseInt(selectedAgent)
  );

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="mr-2 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-800">Due Amount Transfer</h1>
          </div>

          {submitted ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="pt-6 pb-6 text-center px-6">
                <div className="flex flex-col items-center">
                  <div className="rounded-full bg-green-100 p-3 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold mb-2 text-gray-800">Transfer Successful</h2>
                  <p className="text-gray-500 mb-2">
                    Amount of ₹{data.total_due_amount} has been successfully transferred to the lab.
                  </p>
                  {selectedAgentDetails && (
                    <p className="text-sm text-gray-500 mb-6">
                      Delivery Agent: {selectedAgentDetails.user_name}
                    </p>
                  )}
                  <button
                    onClick={handleBackToDashboard}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-md transition-colors"
                  >
                    Back to Dashboard
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Amount Summary */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                    <IndianRupee className="h-5 w-5 mr-2 text-red-600" />
                    Amount Summary
                  </h2>
                </div>
                <div className="px-6 py-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Total Collections Due</span>
                      <span className="text-2xl font-bold text-red-600">₹{data.total_due_amount}</span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">Based on completed collections pending transfer</div>
                  </div>
                </div>
              </div>

              {/* Transfer Form */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Transfer Details</h2>
                </div>
                <div className="px-6 py-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="total-amount" className="block text-sm font-medium text-gray-700">
                        Total Amount
                      </label>
                      <input
                        id="total-amount"
                        type="text"
                        value={`₹${data.total_due_amount}`}
                        readOnly
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-800 font-semibold"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="delivery-agent" className="block text-sm font-medium text-gray-700">
                        Delivery Agent
                      </label>
                      <select
                        id="delivery-agent"
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="">Select delivery agent</option>
                        {data.cash_collection_agents.map((agent) => (
                          <option key={agent.user_id} value={agent.user_id}>
                            {agent.user_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedAgentDetails && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center mb-2">
                          <Truck className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-sm font-medium text-blue-800">Selected Agent Details</span>
                        </div>
                        <div className="text-sm text-blue-700">
                          <p><strong>Name:</strong> {selectedAgentDetails.user_name}</p>
                          <p><strong>Phone Number:</strong> {selectedAgentDetails.phone_number}</p>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!selectedAgent || isSubmitting}
                      className="w-full h-12 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-md transition-colors"
                    >
                      {isSubmitting
                        ? 'Processing Transfer...'
                        : `Transfer ₹${data.total_due_amount} to Lab`}
                    </button>
                  </form>
                </div>
              </div>

              {/* Notice */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-amber-400 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">Important Notice</h3>
                    <div className="mt-1 text-sm text-amber-700">
                      <p>• Ensure the selected delivery agent is available for pickup</p>
                      <p>• Transfer cannot be reversed once submitted</p>
                      <p>• You will receive a confirmation once the lab receives the amount</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HandoverDue;
